// ===========================================
// DATA STRUCTURES
// ===========================================
const ATTR_SEPARATOR = '|';
function normalizeAttributionPart(value) {
    return value.replaceAll(ATTR_SEPARATOR, '_');
}
export function encodeDamageAttribution(attribution) {
    const ownerType = normalizeAttributionPart(attribution.ownerType);
    const ownerId = normalizeAttributionPart(attribution.ownerId);
    const mechanicType = normalizeAttributionPart(attribution.mechanicType);
    const mechanicId = normalizeAttributionPart(attribution.mechanicId);
    return `${ownerType}${ATTR_SEPARATOR}${ownerId}${ATTR_SEPARATOR}${mechanicType}${ATTR_SEPARATOR}${mechanicId}`;
}
export function decodeDamageAttribution(key) {
    const [ownerType, ownerId, mechanicType, mechanicId] = key.split(ATTR_SEPARATOR);
    return {
        ownerType: (ownerType || 'system'),
        ownerId: ownerId || 'unknown',
        mechanicType: (mechanicType || 'other'),
        mechanicId: mechanicId || 'unknown',
    };
}
// ===========================================
// MAIN ANALYTICS SYSTEM
// ===========================================
export class AnalyticsSystem {
    currentWaveStats = null;
    sessionHistory = [];
    // Temporary storage for current tick/calculation
    constructor() {
        this.reset();
    }
    reset() {
        this.sessionHistory = [];
        this.currentWaveStats = null;
        this.resetPending();
    }
    resetPending() {
    }
    // --- Wave Management ---
    startWave(waveNumber, currentFortressHp) {
        this.currentWaveStats = {
            waveNumber,
            durationTicks: 0,
            enemiesSpawned: 0,
            enemiesKilled: 0,
            enemiesLeaked: 0,
            fortressDamageTaken: 0,
            fortressHealthEnd: currentFortressHp,
            totalDamageDealt: 0,
            damageBySource: {
                hero: 0,
                turret: 0,
                projectile: 0,
                fortress: 0,
                dot: 0,
                skill: 0,
            },
            damageByHero: {},
            damageByTurret: {},
            damageByAttribution: {},
            damageTakenByHero: {},
            healingBySource: {},
            economy: {
                goldEarned: 0,
                goldSpent: 0,
                dustEarned: 0,
                dustSpent: 0,
            },
        };
    }
    endWave(currentFortressHp) {
        if (!this.currentWaveStats)
            return null;
        this.currentWaveStats.fortressHealthEnd = currentFortressHp;
        this.sessionHistory.push(this.currentWaveStats);
        // Return copy to avoid mutation reference issues if stored elsewhere
        const finalStats = { ...this.currentWaveStats };
        this.currentWaveStats = null;
        return finalStats;
    }
    updateTick() {
        if (this.currentWaveStats) {
            this.currentWaveStats.durationTicks++;
            this.notifyListeners();
        }
    }
    // --- Tracking Methods ---
    trackSpawn() {
        if (this.currentWaveStats) {
            this.currentWaveStats.enemiesSpawned++;
        }
    }
    trackKill() {
        if (this.currentWaveStats) {
            this.currentWaveStats.enemiesKilled++;
        }
    }
    trackLeak() {
        if (this.currentWaveStats) {
            this.currentWaveStats.enemiesLeaked++;
        }
    }
    trackFortressDamage(amount) {
        if (this.currentWaveStats) {
            this.currentWaveStats.fortressDamageTaken += amount;
        }
    }
    /**
     * Track damage dealt to enemies
     * @param sourceType - General source category
     * @param sourceId - Specific ID (e.g. 'storm')
     * @param amount - Damage amount
     */
    trackDamage(sourceType, sourceId, amount) {
        if (!this.currentWaveStats)
            return;
        // Update aggregate totals
        this.currentWaveStats.totalDamageDealt += amount;
        this.currentWaveStats.damageBySource[sourceType] =
            (this.currentWaveStats.damageBySource[sourceType] || 0) + amount;
        // Update specific breakdowns
        if (sourceType === 'hero' || sourceType === 'skill') {
            this.currentWaveStats.damageByHero[sourceId] =
                (this.currentWaveStats.damageByHero[sourceId] || 0) + amount;
        }
        else if (sourceType === 'turret') {
            this.currentWaveStats.damageByTurret[sourceId] =
                (this.currentWaveStats.damageByTurret[sourceId] || 0) + amount;
        }
    }
    trackAttributedDamage(attribution, amount, sourceTypeOverride) {
        if (!this.currentWaveStats)
            return;
        const key = encodeDamageAttribution(attribution);
        this.currentWaveStats.damageByAttribution[key] =
            (this.currentWaveStats.damageByAttribution[key] || 0) + amount;
        const sourceType = sourceTypeOverride ?? this.mapAttributionToSourceType(attribution);
        this.trackDamage(sourceType, attribution.ownerId, amount);
    }
    trackDamageTaken(heroId, amount) {
        if (!this.currentWaveStats)
            return;
        this.currentWaveStats.damageTakenByHero[heroId] =
            (this.currentWaveStats.damageTakenByHero[heroId] || 0) + amount;
    }
    trackHealing(sourceId, amount) {
        if (!this.currentWaveStats)
            return;
        this.currentWaveStats.healingBySource[sourceId] =
            (this.currentWaveStats.healingBySource[sourceId] || 0) + amount;
    }
    trackEconomy(type, resource, amount) {
        if (!this.currentWaveStats)
            return;
        if (type === 'earn') {
            if (resource === 'gold')
                this.currentWaveStats.economy.goldEarned += amount;
            if (resource === 'dust')
                this.currentWaveStats.economy.dustEarned += amount;
        }
        else {
            if (resource === 'gold')
                this.currentWaveStats.economy.goldSpent += amount;
            if (resource === 'dust')
                this.currentWaveStats.economy.dustSpent += amount;
        }
    }
    // --- Event Handling ---
    listeners = [];
    onUpdate(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
    notifyListeners() {
        for (const listener of this.listeners) {
            listener();
        }
    }
    mapAttributionToSourceType(attribution) {
        if (attribution.mechanicType === 'dot') {
            return 'dot';
        }
        switch (attribution.ownerType) {
            case 'hero':
                return attribution.mechanicType === 'basic' ? 'hero' : 'skill';
            case 'turret':
                return 'turret';
            case 'fortress':
                return 'fortress';
            case 'system':
            default:
                return 'skill';
        }
    }
    // --- Retrieval ---
    getCurrentWaveStats() {
        return this.currentWaveStats;
    }
    getSessionHistory() {
        return this.sessionHistory;
    }
    /**
     * Generates a summary text report for the current/last wave
     */
    generateReport() {
        const stats = this.currentWaveStats || this.sessionHistory[this.sessionHistory.length - 1];
        if (!stats)
            return "No data available.";
        const totalDmg = Math.max(1, stats.totalDamageDealt);
        const heroDmgPct = ((stats.damageBySource.hero + stats.damageBySource.skill) / totalDmg * 100).toFixed(1);
        const turretDmgPct = (stats.damageBySource.turret / totalDmg * 100).toFixed(1);
        // Find MVP Hero
        let mvpHero = 'None';
        let maxHeroDmg = 0;
        for (const [hero, dmg] of Object.entries(stats.damageByHero)) {
            if (dmg > maxHeroDmg) {
                maxHeroDmg = dmg;
                mvpHero = hero;
            }
        }
        return `
=== Wave ${stats.waveNumber} Report ===
Enemies: ${stats.enemiesKilled}/${stats.enemiesSpawned} (Leaked: ${stats.enemiesLeaked})
Fortress: -${stats.fortressDamageTaken} HP
Total Damage: ${stats.totalDamageDealt}
  - Heroes: ${heroDmgPct}% (MVP: ${mvpHero})
  - Turrets: ${turretDmgPct}%
Economy: +${stats.economy.goldEarned} Gold / -${stats.economy.goldSpent} Spent
========================
    `.trim();
    }
}
// Global instance (singleton-like for simulation usage)
export const analytics = new AnalyticsSystem();
//# sourceMappingURL=analytics.js.map